import {
  Box,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Select,
  MenuItem,
  IconButton,
  Chip,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ButtonComp from "../../../../components/Button";
import dayjs from "dayjs";
import Loader from "../../../../components/Loader";
import { useEffect, useState } from "react";
import HistoryTabSwitch from "./HistoryTabSwitch";
import {
  StandardCountBadge,
  StatusDotChip,
  standardTableHeadCellSx,
  standardTableRowSx,
} from "../../ifas/components/StandardUI";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import {
  getClaimNotifications,
  getTransactionHistory,
} from "../../../../services/dashboard";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { useToast } from "../../../../hooks/useToast";
import {
  OperationsEmptyState,
  OperationsPageShell,
  ifasColors,
  operationsDetailPaperSx,
  operationsRowSx,
  operationsTableHeadSx,
  operationsTablePaperSx,
} from "../../ifas/components/OperationsUI";

const History = ({
  onBack,
  historyData,
  isLoading,
  claimDetails,
  historyType = "claim",
}: {
  onBack: () => void;
  historyData: any;
  isLoading: boolean;
  claimDetails?: any;
  historyType?: "claim" | "case";
}) => {
  const [selectedTab, setSelectedTab] = useState(
    historyType === "case" ? "case-history" : "claim-history",
  );
  const [loading, setLoading] = useState(false);
  const [transactionHistoryData, setTransactionHistoryData] = useState([]);
  const [notificationData, setNotificationData] = useState<any[]>([]);
  const [notificationsLoaded, setNotificationsLoaded] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { showInfoToast } = useToast();

  useEffect(() => {
    setSelectedTab(historyType === "case" ? "case-history" : "claim-history");
  }, [historyType]);

  const paginatedData = transactionHistoryData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage,
  );
  const totalPages = Math.ceil(transactionHistoryData.length / rowsPerPage);

  const onTransactionHistoryHandle = async () => {
    const policyNumber = claimDetails.data.policyNumber;
    setLoading(true);
    try {
      const res = await getTransactionHistory(policyNumber, 0, 1000);
      setTransactionHistoryData(res.data);
    } catch (err: any) {
      showInfoToast(err);
    }
    setLoading(false);
  };

  const onNotificationsHandle = async () => {
    const claimId = claimDetails?.data?.claimId || claimDetails?.data?.id;
    if (!claimId || notificationsLoaded) return;
    setLoading(true);
    try {
      const res = await getClaimNotifications(claimId);
      setNotificationData(Array.isArray(res?.data) ? res.data : []);
      setNotificationsLoaded(true);
    } catch (err: any) {
      showInfoToast(err);
    }
    setLoading(false);
  };

  const timelineItems = Array.isArray(historyData) ? historyData : [];
  const paginationPages = Math.max(totalPages, 1);
  const historyTitle =
    selectedTab === "transaction-history"
      ? "Transaction History"
      : selectedTab === "notifications"
        ? "Claimant Notifications"
        : historyType === "case"
          ? "Activity History"
          : "Claim History";

  const getHistoryDate = (item: any) => {
    const date =
      item.updated_on || item.created_on || item.updatedAt || item.createdAt;

    return date ? dayjs(date).format("YYYY-MM-DD HH:mm:ss") : "Date not captured";
  };

  const getAuditActionLabel = (item: any) => {
    const action = String(item?.metadata?.auditAction || item?.eventType || "ACTIVITY");
    const labels: Record<string, string> = {
      CASH_CLIENT_CONVERTED_TO_POLICYHOLDER: "Cash to Policyholder",
      CLAIM_LINKED_TO_EXISTING_CASE: "Claim Auto-linked",
      DUPLICATE_CASE_CREATION_PREVENTED: "Duplicate Case Prevented",
      CREATE_CASE: "Case Created",
      UPDATE_CASE: "Case Updated",
      DECLINE_CASE: "Case Declined",
    };
    return labels[action] || action.replaceAll("_", " ");
  };

  const getAuditReference = (item: any) => {
    const metadata = item?.metadata || {};
    return metadata.iceClaimNumber || metadata.incomingIceClaimNumber || null;
  };

  return (
    <OperationsPageShell
      eyebrow={historyType === "case" ? "Case Timeline" : "Claim Timeline"}
      title={historyTitle}
      description="Review each captured activity in order, with timestamps and transaction details kept easy to scan."
      meta={
        <Chip
          label={`${timelineItems.length} events`}
          size="small"
          sx={{
            bgcolor: "#FFF4EB",
            color: ifasColors.orange,
            fontWeight: 800,
          }}
        />
      }
      actions={
        <ButtonComp
          variant="outlined"
          height={38}
          fontSize="14px"
          width={120}
          onClick={onBack}
        >
          Back
        </ButtonComp>
      }
    >
      {isLoading ? <Loader isLoading={isLoading} /> : null}
      {loading ? <Loader isLoading={loading} /> : null}
      <Paper
        elevation={0}
        sx={operationsDetailPaperSx}
      >
        {historyType === "claim" && (
          <HistoryTabSwitch
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            tabs={[
              { value: "claim-history", label: "Claim History" },
              {
                value: "transaction-history",
                label: "Transactions",
                onSelect: onTransactionHistoryHandle,
              },
              {
                value: "notifications",
                label: "Notifications",
                onSelect: onNotificationsHandle,
              },
            ]}
          />
        )}
        {(selectedTab === "claim-history" || selectedTab === "case-history") && (
          <Box position="relative" p={{ xs: 1, md: 1.5 }} mt={2}>
            {timelineItems.length === 0 ? (
              <OperationsEmptyState
                title="No history events found"
                description="Activity will appear here as the record moves through the workflow."
              />
            ) : (
              timelineItems.map((item: any, index: any) => (
                <Box
                  key={index}
                  display="flex"
                  alignItems="flex-start"
                  position="relative"
                  mb={index !== timelineItems.length - 1 ? 1.5 : 0}
                >
                  {index !== timelineItems.length - 1 && (
                    <Box
                      position="absolute"
                      top={34}
                      left={17}
                      width={2}
                      height="100%"
                      bgcolor={
                        item.status ? ifasColors.success : ifasColors.border
                      }
                    />
                  )}

                  <Box
                    mr={1.5}
                    mt={0.35}
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      bgcolor: item.status ? "#EAF7EF" : "#F1F1F1",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flex: "0 0 auto",
                      zIndex: 1,
                    }}
                  >
                    <CheckCircleIcon
                      sx={{
                        color: item.status ? ifasColors.success : "#A6A6A6",
                        fontSize: 22,
                      }}
                    />
                  </Box>

                  <Paper
                    elevation={0}
                    sx={{
                      width: "100%",
                      p: { xs: 1.5, md: 1.75 },
                      borderRadius: 2,
                      border: `1px solid ${ifasColors.border}`,
                      bgcolor: "#FFFFFF",
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} flexWrap="wrap">
                      <Box flex={1} minWidth={220}>
                        <Typography fontSize={15} fontWeight={700} color={ifasColors.text}>
                          {item.eventDescription ||
                            item.event_description ||
                            "Activity captured"}
                        </Typography>
                        <Typography fontSize={12.5} color={ifasColors.muted} mt={0.4}>
                          {getHistoryDate(item)}
                          {item.performedBy || item.performed_by
                            ? ` · By ${item.performedBy || item.performed_by}`
                            : ""}
                          {item.roleAtTime || item.role_at_time
                            ? ` · ${item.roleAtTime || item.role_at_time}`
                            : ""}
                        </Typography>
                      </Box>
                      {historyType === "case" && (
                        <Chip
                          size="small"
                          label={getAuditActionLabel(item)}
                          sx={{ bgcolor: "#FFF4EB", color: ifasColors.orange, fontWeight: 800 }}
                        />
                      )}
                    </Box>

                    {historyType === "case" && item.metadata && (
                      <Box
                        mt={1.1}
                        pt={1}
                        display="flex"
                        flexWrap="wrap"
                        gap={0.75}
                        sx={{ borderTop: `1px solid ${ifasColors.border}` }}
                      >
                        {getAuditReference(item) && (
                          <Chip size="small" variant="outlined" label={`Claim: ${getAuditReference(item)}`} />
                        )}
                        {item.metadata.policyNumber && (
                          <Chip size="small" variant="outlined" label={`Policy: ${item.metadata.policyNumber}`} />
                        )}
                        {item.metadata.previousSourceType && item.metadata.currentSourceType && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`${item.metadata.previousSourceType} → ${item.metadata.currentSourceType}`}
                          />
                        )}
                        {item.metadata.linkMode && (
                          <Chip size="small" variant="outlined" label="Automatic Death Claim Link" />
                        )}
                        {item.metadata.matchedBy && (
                          <Chip size="small" variant="outlined" label="Matched by ID / Passport" />
                        )}
                        {item.metadata.conversionReason && (
                          <Typography width="100%" fontSize={12.5} color={ifasColors.muted}>
                            Reason: {item.metadata.conversionReason}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Paper>
                </Box>
              ))
            )}
          </Box>
        )}
        {historyType === "claim" && selectedTab === "notifications" && (
          <Box position="relative" p={2} mt={2}>
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              mb={1.5}
              flexWrap="wrap"
            >
              <Typography fontSize={15} fontWeight={800} color={ifasColors.text}>
                Messages sent to the claimant
              </Typography>
              <StandardCountBadge count={notificationData.length} />
              <Typography fontSize={12.5} color={ifasColors.muted}>
                Email, SMS and WhatsApp, most recent first
              </Typography>
            </Box>
            {notificationData.length === 0 ? (
              <OperationsEmptyState
                title="No notifications sent yet"
                description="Messages sent to the claimant for this claim will appear here as the claim moves through the workflow."
              />
            ) : (
              <Paper sx={operationsTablePaperSx} elevation={0}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        {["Sent", "Channel", "Recipient", "Message"].map(
                          (header) => (
                            <TableCell key={header} sx={standardTableHeadCellSx}>
                              {header}
                            </TableCell>
                          ),
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {notificationData.map((item: any, index: number) => (
                        <TableRow key={index} sx={standardTableRowSx}>
                          <TableCell>
                            {item.sentOn
                              ? dayjs(item.sentOn).format("YYYY-MM-DD HH:mm")
                              : "Date not captured"}
                          </TableCell>
                          <TableCell>
                            <StatusDotChip
                              status={item.channel}
                              tone={
                                item.channel === "WHATSAPP"
                                  ? "green"
                                  : item.channel === "SMS"
                                    ? "blue"
                                    : "grey"
                              }
                            />
                          </TableCell>
                          <TableCell>{item.recipient || "-"}</TableCell>
                          <TableCell
                            sx={{
                              whiteSpace: "normal",
                              maxWidth: 520,
                              color: ifasColors.muted,
                              fontSize: 13,
                            }}
                          >
                            {item.message || "-"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </Box>
        )}
        {historyType === "claim" && selectedTab === "transaction-history" && (
          <Box position="relative" p={2} mt={2}>
            <Paper sx={operationsTablePaperSx} elevation={0}>
            <TableContainer>
              <Table>
                <TableHead sx={operationsTableHeadSx}>
                  <TableRow>
                    {[
                      "Transaction Date",
                      "Month/Year",
                      "Amount",
                      "Payment Method",
                    ].map((header) => (
                      <TableCell key={header}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          {header}
                          <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData?.map((item: any, idx) => (
                    <TableRow key={idx} sx={operationsRowSx}>
                      <TableCell>{item.transaction_date}</TableCell>
                      <TableCell>{item.month_year}</TableCell>
                      <TableCell>{item.amount}</TableCell>
                      <TableCell>{item.payment_method}</TableCell>
                    </TableRow>
                  ))}
                  {paginatedData.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <OperationsEmptyState
                          title="No transactions found"
                          description="Payment transactions will appear here once available."
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            </Paper>

            {/* Pagination Controls */}
            <Box
              display="flex"
              justifyContent="space-between"
              mt={2}
              alignItems="center"
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Select
                  size="small"
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setPage(1);
                  }}
                  displayEmpty
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
                <Typography>per page</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Select
                  size="small"
                  value={page}
                  onChange={(e) => setPage(Number(e.target.value))}
                >
                  {Array.from({ length: paginationPages }, (_, i) => (
                    <MenuItem key={i + 1} value={i + 1}>
                      {i + 1}
                    </MenuItem>
                  ))}
                </Select>
                <Typography>of {paginationPages} pages</Typography>
                <IconButton
                  onClick={() => setPage((p) => Math.max(p - 1, 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft />
                </IconButton>
                <IconButton
                  onClick={() => setPage((p) => Math.min(p + 1, paginationPages))}
                  disabled={page === paginationPages}
                >
                  <ChevronRight />
                </IconButton>
              </Box>
            </Box>
          </Box>
        )}
      </Paper>
    </OperationsPageShell>
  );
};

export default History;
